import {useEffect, useRef, useState} from "react";
import {FiPlus} from "react-icons/fi";

import {Accordion} from "@/components/accordion/Accordion.tsx";
import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {MetricsExplorerSchema} from "@/lib/types/metricsExplorer.ts";
import {GuessedField} from "@/pages/metricsExplorer/catalogDefaults.ts";
import {ExplorerConfig, ExplorerSeriesConfig, MAX_SERIES} from "@/pages/metricsExplorer/explorerConfig.ts";
import {uniqueCheckTypes} from "@/pages/metricsExplorer/schemaUtils.ts";
import {SeriesSpecRow} from "@/pages/metricsExplorer/seriesBuilder/SeriesSpecRow.tsx";

import styles from "./SeriesBuilderRail.module.scss";

interface SeriesBuilderRailProps {
  config: ExplorerConfig;
  schema: MetricsExplorerSchema | undefined;
  checkMeta: Record<number, CheckInfo>;
  start: Date;
  end: Date;
  onUpdateSeries: (ref: string, patch: Partial<ExplorerSeriesConfig>) => void;
  /** Per series ref, the fields the catalog filled by a guess the user has not confirmed. */
  guessedFields: Record<string, GuessedField[]>;
  /** The letter each row goes by on the chart's legend. */
  seriesLetters: Record<string, string>;
  onAddSeries: () => string | null;
  onRemoveSeries: (ref: string) => void;
  onDuplicateSeries: (ref: string) => string | null;
  onChecksResolved: (checks: CheckInfo[]) => void;
}

/**
 * The series panel: one row per series, with only the row being edited expanded so the
 * list stays short, and a header that never scrolls away.
 */
export const SeriesBuilderRail = ({
  config,
  schema,
  checkMeta,
  start,
  end,
  onUpdateSeries,
  guessedFields,
  seriesLetters,
  onAddSeries,
  onRemoveSeries,
  onDuplicateSeries,
  onChecksResolved,
}: SeriesBuilderRailProps) => {
  // Undefined until the user picks: the first series starts open so a new chart shows its
  // controls. Null means they collapsed everything.
  const [expandedRef, setExpandedRef] = useState<string | null | undefined>(undefined);
  const scrollToRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const openRef = expandedRef === undefined ? config.series[0]?.ref : expandedRef;
  const canAdd = config.series.length < MAX_SERIES;

  // A new series lands at the bottom of a list that scrolls on its own; bring it into view
  // once it has rendered.
  useEffect(() => {
    if (!scrollToRef.current) {
      return;
    }
    listRef.current
      ?.querySelector(`[data-series-ref="${scrollToRef.current}"]`)
      ?.scrollIntoView({behavior: "smooth", block: "nearest"});
    scrollToRef.current = null;
  }, [config.series]);

  const openNew = (ref: string | null) => {
    if (ref) {
      setExpandedRef(ref);
      scrollToRef.current = ref;
    }
  };

  return (
    <Card variant="white" className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <span className={styles.title}>Series</span>
          <Chip variant="count">
            {config.series.length} / {MAX_SERIES}
          </Chip>
        </div>
        <Button
          variant="secondary"
          size="small"
          onClick={() => openNew(onAddSeries())}
          disabled={!canAdd}
          title={canAdd ? undefined : `A chart holds up to ${MAX_SERIES} series`}
        >
          <Icon icon={FiPlus} /> Add series
        </Button>
      </div>

      <Accordion activeKey={openRef} className={styles.list} ref={listRef}>
        {config.series.map((series) => (
          <SeriesSpecRow
            key={series.ref}
            series={series}
            checkNames={series.checkIds.map((id) => checkMeta[id]?.name).filter((name): name is string => !!name)}
            guessedFields={guessedFields[series.ref] ?? []}
            letter={seriesLetters[series.ref]}
            isExpanded={series.ref === openRef}
            canDuplicate={canAdd}
            onToggleExpanded={() => setExpandedRef(series.ref === openRef ? null : series.ref)}
            schema={schema}
            checkTypes={uniqueCheckTypes(
              series.checkIds
                .map((id) => checkMeta[id]?.monitoring_service_type)
                .filter((type): type is string => !!type)
            )}
            start={start}
            end={end}
            onChange={(patch) => onUpdateSeries(series.ref, patch)}
            onRemove={() => onRemoveSeries(series.ref)}
            onDuplicate={() => openNew(onDuplicateSeries(series.ref))}
            onChecksResolved={onChecksResolved}
          />
        ))}
      </Accordion>
    </Card>
  );
};
